import { useCallback, useEffect, useMemo, useState } from "react";
import { WithSuperAuth } from "../S-Overview";
import { HOST_URL, getCookie } from "../../../assets/js/helpFunction";
import "./wallet-operations.css";
import "./wallet-operations-extended.css";

const API_ROOT = `${HOST_URL()}estate_payment/admin/`;

const Icon = ({ name, size = 18 }) => {
  const paths = {
    wallet:
      "M3 6.5h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2h14v3M16 12h5m-3 0h.01",
    activity: "M3 12h4l2.5-7 5 14 2.5-7h4",
    alert:
      "M12 9v4m0 4h.01M10.3 3.9 1.8 18.2A2 2 0 0 0 3.5 21h17a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0Z",
    check: "m5 12 4 4L19 6",
    search: "m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z",
    download: "M12 3v12m0 0 4-4m-4 4-4-4M4 20h16",
    upload: "M12 16V4m0 0-4 4m4-4 4 4M4 20h16",
    refresh:
      "M20 7v5h-5M4 17v-5h5m10.5 0A8 8 0 0 0 6 6l-2 2m14 8-2 2A8 8 0 0 1 4.5 12",
    close: "m6 6 12 12M18 6 6 18",
    arrow: "M5 12h14m-5-5 5 5-5 5",
    file: "M6 2h8l4 4v16H6V2Zm8 0v5h5",
    play: "m8 5 11 7-11 7V5Z",
    user: "M20 21a8 8 0 0 0-16 0m8-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
    chevron: "m9 18 6-6-6-6",
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d={paths[name]}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const apiRequest = async (path, options = {}) => {
  const { method = "GET", body, formData = false, download = false } = options;
  const headers = new Headers({
    Authorization: `Bearer ${getCookie("access")}`,
  });
  if (!formData && body !== undefined)
    headers.set("Content-Type", "application/json");
  const response = await fetch(
    path.startsWith("http") ? path : `${API_ROOT}${path}`,
    {
      method,
      headers,
      body:
        body === undefined ? undefined : formData ? body : JSON.stringify(body),
    },
  );
  if (download && response.ok) return { blob: await response.blob(), response };
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const firstFieldError = !Array.isArray(payload)
      ? Object.entries(payload).find(([, value]) => Array.isArray(value) || typeof value === "string")
      : null;
    const error = new Error(
      payload.detail ||
        payload.non_field_errors?.[0] ||
        (Array.isArray(payload) ? payload[0] : null) ||
        (firstFieldError ? `${titleCase(firstFieldError[0])}: ${Array.isArray(firstFieldError[1]) ? firstFieldError[1][0] : firstFieldError[1]}` : null) ||
        "The request could not be completed.",
    );
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
};

const listResults = (payload) => payload?.results || [];
const titleCase = (value = "") =>
  value
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const [whole, fraction = "00"] = String(value).replace(/,/g, "").split(".");
  return `₦${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${fraction.padEnd(2, "0").slice(0, 2)}`;
};
const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "Africa/Lagos",
      }).format(new Date(value))
    : "—";
const metric = (source, ...keys) =>
  keys.reduce((value, key) => value ?? source?.[key], undefined) ?? 0;
const groupCount = (groups, field, value) =>
  groups?.find((item) => item[field] === value)?.count || 0;
const TRANSACTION_FILTER_KEYS = ["search", "reference", "type", "status", "provider", "channel", "funding_source", "service", "currency", "user_id", "wallet_id", "account_scope", "failure_reason", "date_from", "date_to", "timezone", "preset"];
const CASE_FILTER_KEYS = ["search", "match_status", "review_status", "finding", "provider", "account_scope", "user_id", "wallet_id", "type", "status", "channel", "funding_source", "currency", "date_from", "date_to", "timezone", "preset"];

const views = [
  ["overview", "Overview"],
  ["transactions", "Transactions"],
  ["cases", "Reconciliation"],
  ["wallets", "Wallets"],
  ["statements", "Statements"],
  ["evidence", "Provider evidence"],
  ["runs", "Run history"],
  ["reports", "Reports"],
];

const WalletOperations = () => {
  const [activeView, setActiveView] = useState("overview");
  const [overviewPreset, setOverviewPreset] = useState("today");
  const [dashboard, setDashboard] = useState({});
  const [reconciliation, setReconciliation] = useState({});
  const [transactions, setTransactions] = useState({ results: [] });
  const [cases, setCases] = useState({ results: [] });
  const [wallets, setWallets] = useState({ results: [] });
  const [imports, setImports] = useState({ results: [] });
  const [providerRecords, setProviderRecords] = useState({ results: [] });
  const [runs, setRuns] = useState({ results: [] });
  const [reports, setReports] = useState({ results: [] });
  const [loading, setLoading] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    type: "",
    provider: "",
    match_status: "",
    review_status: "",
    finding: "",
    reference: "",
    channel: "",
    funding_source: "",
    service: "",
    currency: "",
    user_id: "",
    wallet_id: "",
    account_scope: "",
    failure_reason: "",
    date_from: "",
    date_to: "",
    timezone: "Africa/Lagos",
    preset: "",
    import_batch_id: "",
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [selectedImport, setSelectedImport] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [walletLedger, setWalletLedger] = useState({ results: [] });
  const [showRun, setShowRun] = useState(false);
  const [runError, setRunError] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [reportError, setReportError] = useState("");
  const [caseAction, setCaseAction] = useState("");
  const [actionForm, setActionForm] = useState({
    reason: "",
    resolution: "",
    assignee_id: "",
    activity_id: "",
  });

  const setBusy = (key, value) =>
    setLoading((current) => ({ ...current, [key]: value }));
  const handleError = (requestError) => {
    if (requestError.status === 401)
      setError("Your session has expired. Please sign in again.");
    else if (requestError.status === 403)
      setError("This area requires an active super-admin account.");
    else setError(requestError.message);
  };

  const queryString = useCallback(
    (kind, page = 1) => {
      const params = new URLSearchParams({
        page: String(page),
        page_size: "50",
      });
      if (kind === "transactions") {
        TRANSACTION_FILTER_KEYS.forEach((key) => filters[key] && params.set(key, filters[key]));
      }
      if (kind === "cases") {
        CASE_FILTER_KEYS.forEach((key) => filters[key] && params.set(key, filters[key]));
      }
      if (kind === "wallets") {
        ["search", "user_id", "wallet_id"].forEach((key) => filters[key] && params.set(key, filters[key]));
      }
      if (kind === "evidence") {
        ["search", "provider", "channel", "account_scope", "status", "import_batch_id"].forEach((key) => filters[key] && params.set(key, filters[key]));
      }
      return params.toString();
    },
    [filters],
  );

  const loadOverview = useCallback(async () => {
    setBusy("overview", true);
    setError("");
    try {
      const [summary, overview] = await Promise.all([
        apiRequest(`dashboard/?preset=${overviewPreset}`),
        apiRequest("reconciliation/overview/"),
      ]);
      setDashboard(summary);
      setReconciliation(overview);
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setBusy("overview", false);
    }
  }, [overviewPreset]);

  const loadList = useCallback(
    async (kind, page = 1) => {
      setBusy(kind, true);
      setError("");
      const endpoint = kind === "cases" ? "reconciliation/cases/" : kind === "evidence" ? "provider-records/" : `${kind}/`;
      try {
        const payload = await apiRequest(
          `${endpoint}?${queryString(kind, page)}`,
        );
        if (kind === "transactions") setTransactions(payload);
        if (kind === "cases") setCases(payload);
        if (kind === "wallets") setWallets(payload);
        if (kind === "evidence") setProviderRecords(payload);
      } catch (requestError) {
        handleError(requestError);
      } finally {
        setBusy(kind, false);
      }
    },
    [queryString],
  );

  const loadSimpleList = useCallback(async (kind, page = 1) => {
    setBusy(kind, true);
    setError("");
    try {
      const endpoint = kind === "statements" ? "provider-imports/" : kind === "runs" ? "reconciliation/runs/" : "reports/";
      const payload = await apiRequest(`${endpoint}?page=${page}&page_size=50`);
      if (kind === "statements") setImports(payload);
      else if (kind === "runs") setRuns(payload);
      else setReports(payload);
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setBusy(kind, false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);
  useEffect(() => {
    if (activeView === "overview") return;
    const timer = setTimeout(
      () => {
        if (["transactions", "cases", "wallets", "evidence"].includes(activeView))
          loadList(activeView);
        else loadSimpleList(activeView);
      },
      filters.search ? 350 : 0,
    );
    return () => clearTimeout(timer);
  }, [activeView, filters, loadList, loadSimpleList]);

  const openTransaction = async (id) => {
    setBusy("detail", true);
    setError("");
    try {
      setSelectedTransaction(await apiRequest(`transactions/${id}/`));
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setBusy("detail", false);
    }
  };
  const openCase = async (id) => {
    setBusy("detail", true);
    setError("");
    try {
      setSelectedCase(await apiRequest(`reconciliation/cases/${id}/`));
      setCaseAction("");
      setActionForm({
        reason: "",
        resolution: "",
        assignee_id: "",
        activity_id: "",
      });
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setBusy("detail", false);
    }
  };
  const openWallet = async (id) => {
    setBusy("detail", true);
    setError("");
    try {
      const [wallet, ledger] = await Promise.all([
        apiRequest(`wallets/${id}/`),
        apiRequest(`wallets/${id}/transactions/`),
      ]);
      setSelectedWallet(wallet);
      setWalletLedger(ledger);
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setBusy("detail", false);
    }
  };
  const openImport = async (id) => {
    setBusy("detail", true); setError("");
    try {
      const [statement, evidence] = await Promise.all([
        apiRequest(`provider-imports/${id}/`),
        apiRequest(`provider-records/?import_batch_id=${id}&page_size=50`),
      ]);
      setSelectedImport({ ...statement, evidence });
    } catch (requestError) { handleError(requestError); }
    finally { setBusy("detail", false); }
  };
  const openRun = async (id) => {
    setBusy("detail", true); setError("");
    try { setSelectedRun(await apiRequest(`reconciliation/runs/${id}/`)); }
    catch (requestError) { handleError(requestError); }
    finally { setBusy("detail", false); }
  };
  const openReport = async (id) => {
    setBusy("detail", true); setError("");
    try { setSelectedReport(await apiRequest(`reports/${id}/`)); }
    catch (requestError) { handleError(requestError); }
    finally { setBusy("detail", false); }
  };

  const submitCaseAction = async (event) => {
    event.preventDefault();
    if (!caseAction || !actionForm.reason.trim()) return;
    setBusy("action", true);
    setError("");
    const body = {
      action: caseAction,
      reason: actionForm.reason,
      version: selectedCase.version,
    };
    if (caseAction === "RESOLVE") body.resolution = actionForm.resolution;
    if (caseAction === "ASSIGN") body.assignee_id = actionForm.assignee_id;
    if (caseAction === "LINK") body.activity_id = actionForm.activity_id;
    try {
      const key =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const headers = new Headers({
        Authorization: `Bearer ${getCookie("access")}`,
        "Content-Type": "application/json",
        "Idempotency-Key": key,
      });
      const response = await fetch(
        `${API_ROOT}reconciliation/cases/${selectedCase.id}/actions/`,
        { method: "POST", headers, body: JSON.stringify(body) },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const actionError = new Error(
          payload.detail || "The action could not be completed.",
        );
        actionError.status = response.status;
        throw actionError;
      }
      setNotice(`${titleCase(caseAction)} completed successfully.`);
      await openCase(selectedCase.id);
      await Promise.all([loadList("cases"), loadOverview()]);
    } catch (requestError) {
      if (requestError.status === 409)
        setError(
          "This case changed or the action is no longer eligible. The case has been refreshed.",
        );
      else if (requestError.status === 502)
        setError(
          "The provider could not verify this transaction. No financial correction was made.",
        );
      else handleError(requestError);
      if (requestError.status === 409) await openCase(selectedCase.id);
    } finally {
      setBusy("action", false);
    }
  };

  const runReconciliation = async (event) => {
    event.preventDefault();
    setBusy("run", true);
    setRunError("");
    const form = new FormData(event.currentTarget);
    const body = {};
    TRANSACTION_FILTER_KEYS.forEach((key) => {
      const value = form.get(key);
      if (value) body[key] = value;
    });
    try {
      await apiRequest("reconciliation/runs/", { method: "POST", body });
      setNotice("Reconciliation run completed.");
      setShowRun(false);
      await Promise.all([loadOverview(), loadList("cases"), loadSimpleList("runs")]);
    } catch (requestError) {
      if (requestError.status === 401)
        setRunError("Your session has expired. Please sign in again.");
      else if (requestError.status === 403)
        setRunError("This action requires an active super-admin account.");
      else setRunError(requestError.message);
    } finally {
      setBusy("run", false);
    }
  };

  const uploadStatement = async (event) => {
    event.preventDefault();
    setBusy("upload", true);
    setUploadError("");
    const body = new FormData(event.currentTarget);
    ["period_start", "period_end"].forEach((field) => {
      const value = body.get(field);
      if (value) body.set(field, new Date(value).toISOString());
      else body.delete(field);
    });
    try {
      await apiRequest("provider-imports/", {
        method: "POST",
        body,
        formData: true,
      });
      setNotice("Provider statement imported successfully.");
      setShowUpload(false);
      await loadSimpleList("statements");
    } catch (requestError) {
      const rows = requestError.payload?.rows;
      setUploadError(
        rows?.length
          ? `${requestError.payload.error_count || rows.length} row error(s): ${rows
              .slice(0, 3)
              .map((row) => `Row ${row.row}: ${row.error}`)
              .join(" • ")}`
          : requestError.message,
      );
    } finally {
      setBusy("upload", false);
    }
  };

  const createReport = async (event) => {
    event.preventDefault();
    setBusy("report", true);
    setReportError("");
    const form = new FormData(event.currentTarget);
    const reportFilters = {};
    const reportType = form.get("report_type");
    const filterKeys = reportType === "reconciliation"
      ? CASE_FILTER_KEYS
      : reportType === "wallets"
        ? ["search", "user_id", "wallet_id"]
        : TRANSACTION_FILTER_KEYS;
    filterKeys.forEach(
      (key) => filters[key] && (reportFilters[key] = filters[key]),
    );
    try {
      await apiRequest("reports/", {
        method: "POST",
        body: {
          report_type: reportType,
          format: form.get("format"),
          filters: reportFilters,
        },
      });
      setNotice("Report generated successfully.");
      setShowReport(false);
      await loadSimpleList("reports");
    } catch (requestError) {
      if (requestError.status === 401)
        setReportError("Your session has expired. Please sign in again.");
      else if (requestError.status === 403)
        setReportError("This action requires an active super-admin account.");
      else if (requestError.status === 410)
        setReportError("This report request has expired. Generate a new export.");
      else setReportError(requestError.message);
    } finally {
      setBusy("report", false);
    }
  };

  const downloadReport = async (report) => {
    setBusy(`download-${report.id}`, true);
    setError("");
    try {
      const { blob, response } = await apiRequest(`reports/${report.id}/download/`, {
        download: true,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers.get("Content-Disposition") || "";
      const headerFilename = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)?.[1];
      link.download = decodeURIComponent(headerFilename || report.filename || `estateiq-${report.report_type}.${report.format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setNotice("Report download started.");
    } catch (requestError) {
      setError(
        requestError.status === 410
          ? "This report has expired. Generate a new export."
          : requestError.message,
      );
    } finally {
      setBusy(`download-${report.id}`, false);
    }
  };

  const stats = useMemo(
    () => [
      [
        "Total wallet balance",
        formatMoney(dashboard.total_wallet_balance),
        "Current aggregate balance",
        "wallet",
        "blue",
      ],
      [
        "Pending transactions",
        metric(dashboard, "pending_transactions"),
        "Awaiting or processing",
        "activity",
        "amber",
      ],
      [
        "Unmatched activity",
        metric(dashboard, "unmatched_transactions"),
        "Internal records unmatched",
        "alert",
        "red",
      ],
      [
        "Open review cases",
        groupCount(reconciliation.by_review_status, "review_status", "OPEN"),
        "Require investigation",
        "check",
        "green",
      ],
    ],
    [dashboard, reconciliation],
  );

  const pageFor =
    activeView === "transactions"
      ? transactions
      : activeView === "cases"
        ? cases
        : activeView === "evidence"
          ? providerRecords
          : wallets;

  return (
    <WithSuperAuth activeSection="wallet">
      <main className="wallet-ops">
        <div className="wallet-header">
          <div>
            <p className="wallet-eyebrow">FINANCE CONTROL CENTRE</p>
            <h1>Wallet Operations</h1>
            <p>
              Monitor wallet activity, reconcile provider evidence and apply
              controlled corrections.
            </p>
          </div>
          <div className="wallet-header-actions">
            <button
              className="wallet-secondary"
              onClick={() => { setReportError(""); setShowReport(true); }}
            >
              <Icon name="download" /> Generate report
            </button>
            <button className="wallet-primary" onClick={() => { setRunError(""); setShowRun(true); }}>
              <Icon name="play" /> Start reconciliation
            </button>
          </div>
        </div>

        {(error || notice) && (
          <div className={`wallet-banner ${error ? "error" : "success"}`}>
            <span>{error ? <Icon name="alert" /> : <Icon name="check" />}</span>
            <p>{error || notice}</p>
            <button
              onClick={() => {
                setError("");
                setNotice("");
              }}
            >
              <Icon name="close" />
            </button>
          </div>
        )}

        <div className="wallet-view-tabs" role="tablist" aria-label="Wallet operations sections">
          {views.map(([value, label]) => (
            <button
              key={value}
              className={activeView === value ? "active" : ""}
              role="tab"
              aria-selected={activeView === value}
              onClick={() => setActiveView(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {activeView === "overview" && (
          <>
            <section className="wallet-summary-grid">
              {stats.map(([label, value, note, icon, tone]) => (
                <article className="wallet-summary-card" key={label}>
                  <span className={`wallet-summary-icon ${tone}`}>
                    <Icon name={icon} />
                  </span>
                  <div className="wallet-summary-copy">
                    <p>{label}</p>
                    <h2>{loading.overview ? "…" : value}</h2>
                    <span>{note}</span>
                  </div>
                </article>
              ))}
            </section>
            <section className="wallet-overview-grid">
              <article>
                <div className="wallet-section-head">
                  <div>
                    <span>RECONCILIATION HEALTH</span>
                    <h2>Case overview</h2>
                  </div>
                  <select className="wallet-metric-period" value={overviewPreset} onChange={(event) => setOverviewPreset(event.target.value)}>{["today","yesterday","last_7_days","last_30_days","this_month"].map((value) => <option key={value} value={value}>{titleCase(value)}</option>)}</select>
                </div>
                <div className="wallet-health-grid">
                  {[
                    "matched",
                    "unmatched",
                    "discrepancy",
                    "under_review",
                    "escalated",
                    "resolved",
                  ].map((key) => (
                    <div key={key}>
                      <span>{titleCase(key)}</span>
                      <strong>
                        {["matched", "unmatched", "discrepancy"].includes(key)
                          ? groupCount(reconciliation.by_match_status, "match_status", key.toUpperCase())
                          : groupCount(reconciliation.by_review_status, "review_status", key.toUpperCase())}
                      </strong>
                    </div>
                  ))}
                </div>
              </article>
              <article>
                <div className="wallet-section-head">
                  <div>
                    <span>TRANSACTION FLOW</span>
                    <h2>{titleCase(overviewPreset)} performance</h2>
                  </div>
                </div>
                <div className="wallet-flow-list">
                  <div>
                    <span>Total transactions</span>
                    <strong>{metric(dashboard, "total_transactions")}</strong>
                  </div>
                  <div>
                    <span>Successful</span>
                    <strong>
                      {metric(dashboard, "successful_transactions")}
                    </strong>
                  </div>
                  <div>
                    <span>Failed</span>
                    <strong>{metric(dashboard, "failed_transactions")}</strong>
                  </div>
                  <div>
                    <span>Filtered volume</span>
                    <strong>{formatMoney(dashboard.total_volume)}</strong>
                  </div>
                  {dashboard.volume_by_type?.map((item) => <div key={item.type}><span>{titleCase(item.type)} volume</span><strong>{formatMoney(item.volume ?? item.total ?? item.amount)}</strong></div>)}
                </div>
              </article>
            </section>
          </>
        )}

        {["transactions", "cases", "wallets", "evidence"].includes(activeView) && (
          <section className="wallet-table-card">
            <div className="wallet-section-head wallet-list-head">
              <div>
                <span>
                  {activeView === "cases"
                    ? "INVESTIGATION QUEUE"
                    : activeView === "evidence"
                      ? "INDEPENDENT EVIDENCE"
                    : activeView.toUpperCase()}
                </span>
                <h2>
                  {activeView === "cases"
                    ? "Reconciliation cases"
                    : activeView === "evidence"
                      ? "Provider observations"
                    : titleCase(activeView)}
                </h2>
                <p>
                  {pageFor.count || 0} result{pageFor.count === 1 ? "" : "s"}
                </p>
              </div>
              <button onClick={() => loadList(activeView)}>
                <Icon name="refresh" />
              </button>
            </div>
            <div className="wallet-toolbar">
              <label className="wallet-search">
                <Icon name="search" />
                <input
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((current) => ({
                      ...current,
                      search: event.target.value,
                    }))
                  }
                  placeholder="Search references, users, wallet IDs or phone"
                />
              </label>
              <div className="wallet-filters">
                {activeView !== "wallets" && (
                  <select
                    value={filters.provider}
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        provider: event.target.value,
                      }))
                    }
                  >
                    <option value="">All providers</option>
                    <option>9PSB</option>
                    <option>OPTIMUS</option>
                    <option>VTPASS</option>
                    <option>INTERNAL</option>
                  </select>
                )}
                {activeView !== "wallets" && (
                  <select
                    value={filters.status}
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        status: event.target.value,
                      }))
                    }
                  >
                    <option value="">All payment statuses</option>
                    {(activeView === "evidence" ? [
                      "AWAITING_PAYMENT",
                      "PENDING",
                      "PROCESSING",
                      "SUCCESS",
                      "FAILED",
                      "CANCELLED",
                      "EXPIRED",
                      "REVERSED",
                      "REFUNDED",
                      "UNKNOWN",
                    ] : ["AWAITING_PAYMENT","PENDING","PROCESSING","SUCCESS","FAILED","CANCELLED","EXPIRED","REVERSED","REFUNDED"]).map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                )}
                {activeView === "cases" && (
                  <select
                    value={filters.review_status}
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        review_status: event.target.value,
                      }))
                    }
                  >
                    <option value="">All review states</option>
                    {["OPEN", "UNDER_REVIEW", "ESCALATED", "RESOLVED"].map(
                      (value) => (
                        <option key={value}>{value}</option>
                      ),
                    )}
                  </select>
                )}
              </div>
            </div>
            {activeView !== "wallets" && <AdvancedFilters view={activeView} filters={filters} setFilters={setFilters} />}
            {loading[activeView] ? (
              <div className="wallet-empty">Loading {activeView}…</div>
            ) : activeView === "transactions" ? (
              <TransactionsTable
                items={listResults(transactions)}
                onOpen={openTransaction}
              />
            ) : activeView === "cases" ? (
              <CasesTable items={listResults(cases)} onOpen={openCase} />
            ) : activeView === "evidence" ? (
              <EvidenceTable items={listResults(providerRecords)} />
            ) : (
              <WalletsTable items={listResults(wallets)} onOpen={openWallet} />
            )}
            <Pagination
              payload={pageFor}
              onPage={(page) => loadList(activeView, page)}
            />
          </section>
        )}

        {activeView === "statements" && (
          <section className="wallet-table-card">
            <div className="wallet-section-head wallet-list-head">
              <div>
                <span>PROVIDER EVIDENCE</span>
                <h2>Statement imports</h2>
                <p>
                  Upload independent CSV or Excel evidence for reconciliation.
                </p>
              </div>
              <button
                className="wallet-primary"
                onClick={() => { setUploadError(""); setShowUpload(true); }}
              >
                <Icon name="upload" /> Upload statement
              </button>
            </div>
            <ImportsTable
              items={listResults(imports)}
              loading={loading.statements}
              onOpen={openImport}
            />
            <Pagination payload={imports} onPage={(page) => loadSimpleList("statements", page)} />
          </section>
        )}
        {activeView === "runs" && (
          <section className="wallet-table-card">
            <div className="wallet-section-head wallet-list-head"><div><span>COMPARISON HISTORY</span><h2>Reconciliation runs</h2><p>Stored snapshots of each completed comparison scope.</p></div><button className="wallet-primary" onClick={() => { setRunError(""); setShowRun(true); }}><Icon name="play" /> Start reconciliation</button></div>
            <RunsTable items={listResults(runs)} loading={loading.runs} onOpen={openRun} />
            <Pagination payload={runs} onPage={(page) => loadSimpleList("runs", page)} />
          </section>
        )}
        {activeView === "reports" && (
          <section className="wallet-table-card">
            <div className="wallet-section-head wallet-list-head">
              <div>
                <span>PRIVATE EXPORTS</span>
                <h2>Generated reports</h2>
                <p>Exports are authenticated and expire after seven days.</p>
              </div>
              <button
                className="wallet-primary"
                onClick={() => { setReportError(""); setShowReport(true); }}
              >
                <Icon name="file" /> Generate report
              </button>
            </div>
            <ReportsTable
              items={listResults(reports)}
              loading={loading.reports}
              onDownload={downloadReport}
              onOpen={openReport}
              busy={loading}
            />
            <Pagination payload={reports} onPage={(page) => loadSimpleList("reports", page)} />
          </section>
        )}

        {selectedTransaction && (
          <TransactionDrawer
            data={selectedTransaction}
            onClose={() => setSelectedTransaction(null)}
          />
        )}
        {selectedCase && (
          <CaseDrawer
            data={selectedCase}
            onClose={() => setSelectedCase(null)}
            action={caseAction}
            setAction={setCaseAction}
            form={actionForm}
            setForm={setActionForm}
            onSubmit={submitCaseAction}
            busy={loading.action}
          />
        )}
        {selectedWallet && (
          <WalletDrawer
            data={selectedWallet}
            ledger={walletLedger}
            onClose={() => setSelectedWallet(null)}
          />
        )}
        {selectedImport && <ImportDrawer data={selectedImport} onClose={() => setSelectedImport(null)} />}
        {selectedRun && <RunDrawer data={selectedRun} onClose={() => setSelectedRun(null)} />}
        {selectedReport && <ReportDrawer data={selectedReport} onClose={() => setSelectedReport(null)} onDownload={downloadReport} />}
        {showRun && (
          <Modal
            title="Start reconciliation"
            subtitle="Compare supported internal activity with available provider evidence."
            onClose={() => { setRunError(""); setShowRun(false); }}
          >
            <form onSubmit={runReconciliation}>
              {runError && <div className="wallet-modal-error" role="alert"><Icon name="alert" /><p>{runError}</p></div>}
              <Field label="Provider (optional)">
                <select name="provider">
                  <option value="">All providers</option>
                  <option>9PSB</option>
                  <option>OPTIMUS</option>
                  <option>VTPASS</option>
                  <option>INTERNAL</option>
                </select>
              </Field>
              <Field label="User UUID (optional)">
                <input name="user_id" placeholder="Scope the run to one user" />
              </Field>
              <div className="wallet-field-grid"><Field label="Channel (optional)"><select name="channel"><option value="">All channels</option>{["VAS","WAAS","WALLET","VTPASS","TV","ELECTRICITY"].map((value) => <option key={value}>{value}</option>)}</select></Field><Field label="Transaction type"><select name="type"><option value="">All types</option>{["DEPOSIT","WITHDRAWAL","BILL_PAYMENT","REFUND","ADJUSTMENT"].map((value) => <option key={value}>{value}</option>)}</select></Field></div>
              <div className="wallet-field-grid"><Field label="Wallet UUID (optional)"><input name="wallet_id" /></Field><Field label="Account scope (optional)"><input name="account_scope" /></Field></div>
              <div className="wallet-field-grid"><Field label="Date from"><input type="date" name="date_from" /></Field><Field label="Date to"><input type="date" name="date_to" /></Field></div>
              <ModalActions busy={loading.run} label="Run reconciliation" />
            </form>
          </Modal>
        )}
        {showUpload && (
          <StatementModal
            onClose={() => { setUploadError(""); setShowUpload(false); }}
            onSubmit={uploadStatement}
            busy={loading.upload}
            error={uploadError}
          />
        )}
        {showReport && (
          <ReportModal
            onClose={() => { setReportError(""); setShowReport(false); }}
            onSubmit={createReport}
            busy={loading.report}
            error={reportError}
          />
        )}
      </main>
    </WithSuperAuth>
  );
};

const Status = ({ value, kind = "status" }) => (
  <span className={`wallet-pill ${kind}-${String(value).toLowerCase()}`}>
    {titleCase(value || "Unknown")}
  </span>
);
const Empty = ({ text }) => <div className="wallet-empty">{text}</div>;

const TransactionsTable = ({ items, onOpen }) =>
  items.length ? (
    <div className="wallet-table-scroll">
      <table className="wallet-table">
        <thead>
          <tr>
            <th>Transaction</th>
            <th>Customer</th>
            <th>Type</th>
            <th>Provider</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Date</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <strong>
                  {item.reference ||
                    item.provider_reference ||
                    item.source_id}
                </strong>
                <span>{item.channel || item.source_kind}</span>
              </td>
              <td>
                <strong>
                  {item.user?.name || item.user?.email || "Unknown user"}
                </strong>
                <span>{item.wallet_id || item.user?.phone}</span>
              </td>
              <td>{titleCase(item.type)}</td>
              <td>{item.provider || "—"}</td>
              <td className="wallet-amount">{formatMoney(item.amount)}</td>
              <td>
                <Status value={item.status} />
              </td>
              <td>{formatDate(item.created_at)}</td>
              <td>
                <button
                  className="wallet-review"
                  onClick={() => onOpen(item.id)}
                >
                  Review
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty text="No transactions match the current filters." />
  );
const CasesTable = ({ items, onOpen }) =>
  items.length ? (
    <div className="wallet-table-scroll">
      <table className="wallet-table">
        <thead>
          <tr>
            <th>Case</th>
            <th>Transaction / Customer</th>
            <th>Match</th>
            <th>Review</th>
            <th>Findings</th>
            <th>Updated</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <strong>{String(item.id).slice(0, 12)}…</strong>
                <span>Version {item.version}</span>
              </td>
              <td>
                <strong>
                  {item.key || (item.activity ? `Activity ${String(item.activity).slice(0, 8)}…` : "Provider-only evidence")}
                </strong>
                <span>
                  {item.provider_record ? `Evidence ${String(item.provider_record).slice(0, 8)}…` : item.wallet ? `Wallet ${String(item.wallet).slice(0, 8)}…` : "No linked record"}
                </span>
              </td>
              <td>
                <Status value={item.match_status} kind="match" />
              </td>
              <td>
                <Status value={item.review_status} kind="review" />
              </td>
              <td>
                <div className="wallet-findings">
                  {item.findings?.slice(0, 2).map((finding) => (
                    <span key={finding}>{titleCase(finding)}</span>
                  ))}
                  {item.findings?.length > 2 && (
                    <small>+{item.findings.length - 2}</small>
                  )}
                </div>
              </td>
              <td>{formatDate(item.updated_at || item.created_at)}</td>
              <td>
                <button
                  className="wallet-review"
                  onClick={() => onOpen(item.id)}
                >
                  Investigate
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty text="No reconciliation cases match the current filters." />
  );
const WalletsTable = ({ items, onOpen }) =>
  items.length ? (
    <div className="wallet-table-scroll">
      <table className="wallet-table">
        <thead>
          <tr>
            <th>Wallet</th>
            <th>Owner</th>
            <th>Available balance</th>
            <th>Ledger total</th>
            <th>Drift</th>
            <th>Last activity</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <strong>{item.wallet_id}</strong>
                <span>{String(item.id).slice(0, 12)}…</span>
              </td>
              <td>
                <strong>{item.email}</strong>
                <span>{item.user_id}</span>
              </td>
              <td className="wallet-amount">
                {formatMoney(item.available_balance)}
              </td>
              <td>{formatMoney(item.ledger_total)}</td>
              <td>
                <span
                  className={
                    String(item.drift) === "0.00" || Number(item.drift) === 0
                      ? "wallet-drift-clear"
                      : "wallet-drift-alert"
                  }
                >
                  {formatMoney(item.drift)}
                </span>
              </td>
              <td>{formatDate(item.last_transaction)}</td>
              <td>
                <button
                  className="wallet-review"
                  onClick={() => onOpen(item.id)}
                >
                  View ledger
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty text="No wallets match the current filters." />
  );
const ImportsTable = ({ items, loading, onOpen }) =>
  loading ? (
    <Empty text="Loading statement imports…" />
  ) : items.length ? (
    <div className="wallet-table-scroll">
      <table className="wallet-table">
        <thead>
          <tr>
            <th>File</th>
            <th>Provider</th>
            <th>Channel</th>
            <th>Account scope</th>
            <th>Rows</th>
            <th>Coverage</th>
            <th>Imported</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <strong>{item.filename}</strong>
                <span>{String(item.id).slice(0, 12)}…</span>
              </td>
              <td>{item.provider}</td>
              <td>{item.channel}</td>
              <td>{item.account_scope}</td>
              <td>{item.row_count}</td>
              <td>
                <Status value={item.complete ? "COMPLETE" : "PARTIAL"} />
              </td>
              <td>{formatDate(item.created_at)}</td>
              <td><button className="wallet-review" onClick={() => onOpen(item.id)}>Inspect</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty text="No provider statements have been imported." />
  );
const ReportsTable = ({ items, loading, onDownload, onOpen, busy }) =>
  loading ? (
    <Empty text="Loading generated reports…" />
  ) : items.length ? (
    <div className="wallet-table-scroll">
      <table className="wallet-table">
        <thead>
          <tr>
            <th>Report</th>
            <th>Format</th>
            <th>Rows</th>
            <th>Created</th>
            <th>Expires</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <strong>{titleCase(item.report_type)}</strong>
                <span>{String(item.id).slice(0, 12)}…</span>
              </td>
              <td>{String(item.format).toUpperCase()}</td>
              <td>{item.row_count ?? "—"}</td>
              <td>{formatDate(item.created_at)}</td>
              <td>{formatDate(item.expires_at)}</td>
              <td>
                <button className="wallet-review" onClick={() => onOpen(item.id)}>Details</button>{" "}
                <button
                  className="wallet-review"
                  disabled={busy[`download-${item.id}`]}
                  onClick={() => onDownload(item)}
                >
                  <Icon name="download" /> Download
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty text="No reports have been generated." />
  );

const AdvancedFilters = ({ view, filters, setFilters }) => {
  const update = (key, value) => setFilters((current) => ({ ...current, [key]: value, ...(key === "preset" && value ? { date_from: "", date_to: "" } : {}), ...(["date_from", "date_to"].includes(key) && value ? { preset: "" } : {}) }));
  const clear = () => setFilters((current) => ({ ...current, reference: "", type: "", channel: "", funding_source: "", service: "", user_id: "", wallet_id: "", account_scope: "", failure_reason: "", date_from: "", date_to: "", preset: "", match_status: "", finding: "", import_batch_id: "" }));
  return <details className="wallet-advanced"><summary>Advanced filters <span>Precise scope, dates and identifiers</span></summary><div className="wallet-advanced-grid">
    {view !== "evidence" && <><Field label="Channel"><select value={filters.channel} onChange={(e) => update("channel", e.target.value)}><option value="">All channels</option>{["VAS","WAAS","WALLET","VTPASS","TV","ELECTRICITY"].map((v) => <option key={v}>{v}</option>)}</select></Field><Field label="Account scope"><input value={filters.account_scope} onChange={(e) => update("account_scope", e.target.value)} placeholder="merchant or account number" /></Field></>}
    {view === "transactions" && <><Field label="Exact reference"><input value={filters.reference} onChange={(e) => update("reference", e.target.value)} /></Field><Field label="Type"><select value={filters.type} onChange={(e) => update("type", e.target.value)}><option value="">All types</option>{["DEPOSIT","WITHDRAWAL","BILL_PAYMENT","REFUND","ADJUSTMENT"].map((v) => <option key={v}>{v}</option>)}</select></Field><Field label="Service"><input value={filters.service} onChange={(e) => update("service", e.target.value)} placeholder="e.g. ESTATE_BILL" /></Field><Field label="Failure reason"><input value={filters.failure_reason} onChange={(e) => update("failure_reason", e.target.value)} /></Field></>}
    {view === "cases" && <><Field label="Match state"><select value={filters.match_status} onChange={(e) => update("match_status", e.target.value)}><option value="">All match states</option>{["MATCHED","UNMATCHED","DISCREPANCY"].map((v) => <option key={v}>{v}</option>)}</select></Field><Field label="Finding"><select value={filters.finding} onChange={(e) => update("finding", e.target.value)}><option value="">All findings</option>{["MISSING_INTERNAL","MISSING_PROVIDER","EVIDENCE_UNAVAILABLE","EVIDENCE_INCOMPLETE","AMOUNT_MISMATCH","STATUS_MISMATCH","CURRENCY_MISMATCH","DIRECTION_MISMATCH","DUPLICATE","MISSING_DEBIT","LEDGER_AMOUNT_MISMATCH","REFUND_AMOUNT_MISMATCH","OWED_REFUND","REFUNDED_A_SUCCESS","STRANDED","WALLET_DRIFT"].map((v) => <option key={v}>{v}</option>)}</select></Field><Field label="Type"><select value={filters.type} onChange={(e) => update("type", e.target.value)}><option value="">All types</option>{["DEPOSIT","WITHDRAWAL","BILL_PAYMENT","REFUND","ADJUSTMENT"].map((v) => <option key={v}>{v}</option>)}</select></Field></>}
    {view === "evidence" && <><Field label="Channel"><select value={filters.channel} onChange={(e) => update("channel", e.target.value)}><option value="">All channels</option>{["VAS","WAAS","WALLET","VTPASS","TV","ELECTRICITY"].map((v) => <option key={v}>{v}</option>)}</select></Field><Field label="Account scope"><input value={filters.account_scope} onChange={(e) => update("account_scope", e.target.value)} /></Field><Field label="Import batch UUID"><input value={filters.import_batch_id} onChange={(e) => update("import_batch_id", e.target.value)} /></Field></>}
    {view !== "evidence" && <><Field label="Funding source"><input value={filters.funding_source} onChange={(e) => update("funding_source", e.target.value)} placeholder="WALLET or TRANSFER" /></Field><Field label="User UUID"><input value={filters.user_id} onChange={(e) => update("user_id", e.target.value)} /></Field><Field label="Wallet UUID"><input value={filters.wallet_id} onChange={(e) => update("wallet_id", e.target.value)} /></Field><Field label="Preset"><select value={filters.preset} onChange={(e) => update("preset", e.target.value)}><option value="">Custom / all time</option>{["today","yesterday","last_7_days","last_30_days","this_month"].map((v) => <option key={v} value={v}>{titleCase(v)}</option>)}</select></Field><Field label="Date from"><input type="date" value={filters.date_from} onChange={(e) => update("date_from", e.target.value)} /></Field><Field label="Date to"><input type="date" value={filters.date_to} onChange={(e) => update("date_to", e.target.value)} /></Field></>}
  </div><button className="wallet-filter-clear" onClick={clear}>Clear advanced filters</button></details>;
};

const EvidenceTable = ({ items }) => items.length ? <div className="wallet-table-scroll"><table className="wallet-table"><thead><tr><th>Provider reference</th><th>Internal reference</th><th>Provider / source</th><th>Scope</th><th>Amount</th><th>Direction</th><th>Status</th><th>Observed</th></tr></thead><tbody>{items.map((item) => <tr key={item.id}><td><strong>{item.provider_reference}</strong><span>{String(item.id).slice(0, 12)}…</span></td><td>{item.internal_reference || "—"}</td><td><strong>{item.provider}</strong><span>{titleCase(item.source)}</span></td><td><strong>{item.channel}</strong><span>{item.account_scope}</span></td><td className="wallet-amount">{formatMoney(item.amount)}</td><td>{item.direction}</td><td><Status value={item.status} /></td><td>{formatDate(item.observed_at)}</td></tr>)}</tbody></table></div> : <Empty text="No provider observations match these filters." />;
const RunsTable = ({ items, loading, onOpen }) => loading ? <Empty text="Loading reconciliation runs…" /> : items.length ? <div className="wallet-table-scroll"><table className="wallet-table"><thead><tr><th>Run</th><th>Scope</th><th>Internal</th><th>Evidence</th><th>Discrepancies</th><th>Status</th><th>Completed</th><th></th></tr></thead><tbody>{items.map((item) => <tr key={item.id}><td><strong>{String(item.id).slice(0, 12)}…</strong><span>{item.created_by}</span></td><td>{Object.entries(item.filters || {}).map(([k,v]) => `${titleCase(k)}: ${v}`).join(" · ") || "All supported records"}</td><td>{item.summary?.internal_transactions || 0}</td><td>{item.summary?.provider_observations || 0}</td><td>{item.summary?.DISCREPANCY || 0}</td><td><Status value={item.status} /></td><td>{formatDate(item.completed_at)}</td><td><button className="wallet-review" onClick={() => onOpen(item.id)}>Details</button></td></tr>)}</tbody></table></div> : <Empty text="No reconciliation runs have been recorded." />;

const TransactionEvidence = ({ data }) => <><h3 className="wallet-subhead">Linked ledger entries</h3>{data.entries?.length ? <LedgerRows items={data.entries} /> : <Empty text="No linked ledger entries." />}{data.funding_entries?.length ? <><h3 className="wallet-subhead">Funding entries</h3><LedgerRows items={data.funding_entries} /></> : null}<h3 className="wallet-subhead">Provider observations</h3>{data.provider_observations?.length ? <EvidenceCards items={data.provider_observations} /> : <Empty text="No provider evidence is linked." />}{data.webhook_events?.length ? <><h3 className="wallet-subhead">Webhook receipts</h3><div className="wallet-evidence-cards">{data.webhook_events.map((item) => <div key={item.id}><strong>{item.event_id}</strong><span>{item.provider} · {formatDate(item.received_at)}</span><Status value={item.processed ? "PROCESSED" : "RECEIVED"} /></div>)}</div></> : null}{data.timeline?.length ? <Timeline items={data.timeline} /> : null}{data.reconciliation_cases?.length ? <><h3 className="wallet-subhead">Reconciliation cases</h3><div className="wallet-evidence-cards">{data.reconciliation_cases.map((item) => <div key={item.id}><strong>{titleCase(item.match_status)}</strong><span>{item.findings?.map(titleCase).join(", ") || "No findings"}</span><Status value={item.review_status} kind="review" /></div>)}</div></> : null}</>;
const LedgerRows = ({ items }) => <div className="wallet-ledger">{items.map((row) => <div key={row.id}><span className={row.type === "CREDIT" ? "credit" : "debit"}>{row.type}</span><p><strong>{formatMoney(row.amount)}</strong><small>{row.reference}</small></p><p><small>Before</small><strong>{formatMoney(row.balance_before)}</strong></p><p><small>After</small><strong>{formatMoney(row.balance_after)}</strong></p></div>)}</div>;
const EvidenceCards = ({ items }) => <div className="wallet-evidence-cards">{items.map((item) => <div key={item.id}><strong>{item.provider_reference}</strong><span>{item.provider} · {titleCase(item.source)} · {formatMoney(item.amount)}</span><Status value={item.status} /></div>)}</div>;

const ImportDrawer = ({ data, onClose }) => <Drawer title={data.filename} eyebrow="STATEMENT IMPORT" onClose={onClose}><DetailRows rows={[["Provider",data.provider],["Channel",data.channel],["Account scope",data.account_scope],["Coverage",data.complete ? "Complete" : "Partial"],["Period start",formatDate(data.period_start)],["Period end",formatDate(data.period_end)],["Accepted rows",data.row_count],["Imported",formatDate(data.created_at)],["Imported by",data.created_by]]} /><h3 className="wallet-subhead">Accepted provider records</h3><EvidenceCards items={listResults(data.evidence)} /></Drawer>;
const RunDrawer = ({ data, onClose }) => <Drawer title={`Run ${String(data.id).slice(0,12)}…`} eyebrow="RECONCILIATION RUN" onClose={onClose}><div className="wallet-drawer-amount"><span>Run status</span><h3>{titleCase(data.status)}</h3><span>{formatDate(data.completed_at)}</span></div><h3 className="wallet-subhead">Scope</h3><DetailRows rows={Object.entries(data.filters || {}).map(([key,value]) => [titleCase(key),value])} /><h3 className="wallet-subhead">Stored result counts</h3><DetailRows rows={Object.entries(data.summary || {}).map(([key,value]) => [titleCase(key),value])} /></Drawer>;
const ReportDrawer = ({ data, onClose, onDownload }) => <Drawer title={`${titleCase(data.report_type)} report`} eyebrow="PRIVATE EXPORT" onClose={onClose}><div className="wallet-drawer-amount"><span>{String(data.format).toUpperCase()} export</span><h3>{data.row_count} rows</h3><Status value={data.status} /></div><DetailRows rows={[["Report UUID",data.id],["Created",formatDate(data.created_at)],["Expires",formatDate(data.expires_at)],["Created by",data.created_by]]} /><h3 className="wallet-subhead">Applied filters</h3><DetailRows rows={Object.entries(data.filters || {}).map(([key,value]) => [titleCase(key),value])} /><button className="wallet-primary wallet-drawer-download" disabled={data.status === "EXPIRED"} onClick={() => onDownload(data)}><Icon name="download" /> Download report</button></Drawer>;

const Pagination = ({ payload, onPage }) => {
  if (!payload?.next && !payload?.previous) return null;
  const pageFrom = (url) =>
    Number(new URL(url, window.location.origin).searchParams.get("page") || 1);
  return (
    <footer className="wallet-table-footer">
      <p>{payload.count} total results</p>
      <div>
        <button
          disabled={!payload.previous}
          onClick={() => onPage(pageFrom(payload.previous))}
        >
          Previous
        </button>
        <button
          disabled={!payload.next}
          onClick={() => onPage(pageFrom(payload.next))}
        >
          Next
        </button>
      </div>
    </footer>
  );
};

const Drawer = ({ title, eyebrow, onClose, children }) => (
  <div className="wallet-drawer-backdrop" onMouseDown={onClose}>
    <aside
      className="wallet-drawer"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="wallet-drawer-head">
        <div>
          <p>{eyebrow}</p>
          <h2>{title}</h2>
        </div>
        <button onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      {children}
    </aside>
  </div>
);
const DetailRows = ({ rows }) => (
  <div className="wallet-detail-list">
    {rows.map(([label, value]) => (
      <div key={label}>
        <span>{label}</span>
        <strong>{typeof value === "object" && value !== null ? JSON.stringify(value) : value ?? "—"}</strong>
      </div>
    ))}
  </div>
);
const TransactionDrawer = ({ data, onClose }) => (
  <Drawer
    title={
      data.reference || data.provider_reference || "Transaction detail"
    }
    eyebrow="TRANSACTION ACTIVITY"
    onClose={onClose}
  >
    <div className="wallet-drawer-amount">
      <span>
        {titleCase(data.type)} · {data.provider || data.channel}
      </span>
      <h3>{formatMoney(data.amount)}</h3>
      <Status value={data.status} />
    </div>
    <DetailRows
      rows={[
        ["Customer", data.user?.name || data.user?.email],
        ["Internal reference", data.reference],
        ["Source", `${data.source_kind || "—"} / ${data.source_id || "—"}`],
        ["Service", data.service],
        ["Channel", data.channel],
        ["Fee", formatMoney(data.fee)],
        ["Net amount", formatMoney(data.net_amount)],
        ["Provider reference", data.provider_reference],
        ["Direction", data.direction],
        ["Funding source", data.funding_source],
        ["Created", formatDate(data.created_at)],
        ["Completed", formatDate(data.completed_at)],
        ["Failure reason", data.failure_reason],
      ]}
    />
    <TransactionEvidence data={data} />
  </Drawer>
);
const CaseDrawer = ({
  data,
  onClose,
  action,
  setAction,
  form,
  setForm,
  onSubmit,
  busy,
}) => (
  <Drawer
    title={`Case ${String(data.id).slice(0, 12)}…`}
    eyebrow="RECONCILIATION CASE"
    onClose={onClose}
  >
    <div className="wallet-case-status">
      <Status value={data.match_status} kind="match" />
      <Status value={data.review_status} kind="review" />
      <span>Version {data.version}</span>
    </div>
    <div className="wallet-findings wallet-findings-large">
      {data.findings?.map((finding) => (
        <span key={finding}>{titleCase(finding)}</span>
      ))}
    </div>
    <DetailRows rows={[["Assignee", data.assignee],["Resolution", data.resolution],["Internal reference", data.internal_record?.reference],["Internal customer", data.internal_record?.user?.name || data.internal_record?.user?.email],["Provider reference", data.provider_evidence?.provider_reference],["Evidence source", data.provider_evidence?.source],["Evidence observed", formatDate(data.provider_evidence?.observed_at)]]} />
    <h3 className="wallet-subhead">Internal vs provider</h3>
    <div className="wallet-comparison">
      {Object.entries(data.comparison || {}).map(([key, comparison]) => (
        <div key={key}>
          <strong>{titleCase(key)}</strong>
          <span>{comparison?.internal ?? "Not available"}</span>
          <span>{comparison?.provider ?? "Not available"}</span>
          <Status
            value={comparison?.result || "NOT_AVAILABLE"}
            kind="comparison"
          />
        </div>
      ))}
    </div>
    <form className="wallet-action-form" onSubmit={onSubmit}>
      <h3>Case action</h3>
      <Field label="Available action">
        <select
          value={action}
          onChange={(event) => setAction(event.target.value)}
          required
        >
          <option value="">Choose an allowed action</option>
          {data.allowed_actions?.map((item) => (
            <option key={item}>{item}</option>
          ))}
        </select>
      </Field>
      <Field label="Reason">
        <textarea
          value={form.reason}
          onChange={(event) =>
            setForm((current) => ({ ...current, reason: event.target.value }))
          }
          required
          minLength={5}
          maxLength={2000}
          placeholder="Explain the evidence and reason for this action"
        />
      </Field>
      {action === "RESOLVE" && (
        <Field label="Resolution">
          <textarea
            value={form.resolution}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                resolution: event.target.value,
              }))
            }
            required
            maxLength={4000}
          />
        </Field>
      )}
      {action === "ASSIGN" && (
        <Field label="Assignee UUID">
          <input
            value={form.assignee_id}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                assignee_id: event.target.value,
              }))
            }
            required
          />
        </Field>
      )}
      {action === "LINK" && (
        <Field label="Activity UUID">
          <input
            value={form.activity_id}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                activity_id: event.target.value,
              }))
            }
            required
          />
        </Field>
      )}
      {action && <p className="wallet-action-guidance">{({ NOTE:"Adds an audited investigation note only.", ASSIGN:"Assigns an active super-admin and moves the case under review.", INVESTIGATE:"Moves the case under review without changing money.", ESCALATE:"Marks the case escalated; it does not message an external party.", RESOLVE:"Closes the review without changing payment or wallet state.", REOPEN:"Reopens a resolved investigation.", LINK:"Links provider-only evidence after the backend validates identity and amount fields.", REQUERY:"Requests fresh provider status; no resubmission or wallet movement occurs.", SYNC_STATUS:"Synchronizes an eligible pending payment from verified terminal evidence.", REFUND:"Returns the exact eligible original debit once.", RETRY:"Queues only an eligible, funded, never-submitted VAS purchase." })[action]}</p>}
      <button className="wallet-primary" disabled={busy || !action}>
        {busy ? "Applying action…" : "Apply action"}
      </button>
    </form>
    {data.actions?.length ? <Timeline items={data.actions} /> : null}
  </Drawer>
);
const WalletDrawer = ({ data, ledger, onClose }) => (
  <Drawer
    title={data.wallet_id || "Wallet detail"}
    eyebrow="WALLET INVESTIGATION"
    onClose={onClose}
  >
    <div className="wallet-drawer-amount">
      <span>Available balance</span>
      <h3>{formatMoney(data.available_balance)}</h3>
      <span
        className={
          Number(data.drift) === 0 ? "wallet-drift-clear" : "wallet-drift-alert"
        }
      >
        Drift: {formatMoney(data.drift)}
      </span>
    </div>
    <DetailRows
      rows={[
        ["Owner", data.email],
        ["User UUID", data.user_id],
        ["Ledger total", formatMoney(data.ledger_total)],
        [
          "Reserved balance",
          data.reserved_balance === null
            ? "Not supported"
            : formatMoney(data.reserved_balance),
        ],
        ["Last activity", formatDate(data.last_transaction)],
        ["Receiving account", data.receiving_account ? `${data.receiving_account.provider} · ${data.receiving_account.account_number} · ${data.receiving_account.status}` : "Not configured"],
        ["WaaS account", data.waas_account ? `${data.waas_account.account_number} · ${data.waas_account.status} · Tier ${data.waas_account.tier}` : "Not configured"],
      ]}
    />
    <h3 className="wallet-subhead">Ledger history</h3>
    <div className="wallet-ledger">
      {listResults(ledger).map((row) => (
        <div key={row.id}>
          <span className={row.type === "CREDIT" ? "credit" : "debit"}>
            {row.type}
          </span>
          <p>
            <strong>{formatMoney(row.amount)}</strong>
            <small>{formatDate(row.created_at)}</small>
          </p>
          <p>
            <small>Before</small>
            <strong>{formatMoney(row.balance_before)}</strong>
          </p>
          <p>
            <small>After</small>
            <strong>{formatMoney(row.balance_after)}</strong>
          </p>
        </div>
      ))}
    </div>
  </Drawer>
);
const Timeline = ({ items }) => (
  <div className="wallet-timeline">
    <h3>Audit timeline</h3>
    {items.map((item, index) => (
      <div key={item.id || index}>
        <span></span>
        <p>
          <strong>
            {titleCase(item.action || item.kind || item.event_type || item.status)}
          </strong>
          <small>{item.reason || item.detail || item.actor?.name || (item.details ? JSON.stringify(item.details) : "")}</small>
          <time>{formatDate(item.created_at || item.timestamp)}</time>
        </p>
      </div>
    ))}
  </div>
);

const Modal = ({ title, subtitle, onClose, children }) => (
  <div className="wallet-modal-backdrop" onMouseDown={onClose}>
    <section
      className="wallet-modal"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="wallet-modal-head">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <button onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      {children}
    </section>
  </div>
);
const Field = ({ label, children }) => (
  <label className="wallet-field">
    <span>{label}</span>
    {children}
  </label>
);
const ModalActions = ({ busy, label }) => (
  <div className="wallet-modal-actions">
    <button className="wallet-primary" disabled={busy}>
      {busy ? "Please wait…" : label}
    </button>
  </div>
);
const StatementModal = ({ onClose, onSubmit, busy, error }) => (
  <Modal
    title="Upload provider statement"
    subtitle="CSV or XLSX, up to 5 MiB and 10,000 rows."
    onClose={onClose}
  >
    <form onSubmit={onSubmit}>
      {error && <div className="wallet-modal-error" role="alert"><Icon name="alert" /><p>{error}</p></div>}
      <Field label="Statement file">
        <input type="file" name="file" accept=".csv,.xlsx" required />
      </Field>
      <div className="wallet-field-grid">
        <Field label="Provider">
          <select name="provider" required>
            <option value="">Select provider</option>
            <option>9PSB</option>
            <option>OPTIMUS</option>
            <option>VTPASS</option>
          </select>
        </Field>
        <Field label="Channel">
          <select name="channel" required>
            <option value="">Select channel</option>
            <option>VAS</option>
            <option>WAAS</option>
            <option>WALLET</option>
            <option>VTPASS</option>
            <option>TV</option>
            <option>ELECTRICITY</option>
          </select>
        </Field>
      </div>
      <Field label="Account scope">
        <input
          name="account_scope"
          required
          placeholder="merchant or receiving/WaaS account number"
        />
      </Field>
      <div className="wallet-field-grid">
        <Field label="Period start">
          <input type="datetime-local" name="period_start" />
        </Field>
        <Field label="Period end">
          <input type="datetime-local" name="period_end" />
        </Field>
      </div>
      <label className="wallet-check">
        <input type="checkbox" name="complete" value="true" />
        <span>
          This statement completely covers the declared account and period.
        </span>
      </label>
      <ModalActions busy={busy} label="Upload statement" />
    </form>
  </Modal>
);
const ReportModal = ({ onClose, onSubmit, busy, error }) => (
  <Modal
    title="Generate report"
    subtitle="The current search and transaction filters will be included."
    onClose={onClose}
  >
    <form onSubmit={onSubmit}>
      {error && <div className="wallet-modal-error" role="alert"><Icon name="alert" /><p>{error}</p></div>}
      <Field label="Report type">
        <select name="report_type" required>
          {[
            "transactions",
            "reconciliation",
            "wallets",
            "withdrawals",
            "refunds",
            "bill_payments",
          ].map((value) => (
            <option key={value} value={value}>
              {titleCase(value)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Format">
        <select name="format" required>
          <option value="xlsx">Excel (.xlsx)</option>
          <option value="csv">CSV</option>
          <option value="pdf">PDF</option>
        </select>
      </Field>
      <ModalActions busy={busy} label="Generate report" />
    </form>
  </Modal>
);

export default WalletOperations;
